import React from "react";
import { Link } from "react-router-dom";
import LegalPage, { InfoCard } from "@/components/LegalPage";
import {
  Copyright, FileWarning, Reply, UserX, Music4, AlertTriangle, MessageCircle, Info,
} from "lucide-react";

export default function DMCA() {
  const sections = [
    {
      id: "overview", title: "Overview", icon: Info,
      body: (
        <p>
          MystiCast respects creators and intellectual property. We comply with the Digital Millennium Copyright Act (DMCA)
          and equivalent laws in other jurisdictions. If your work has been used without permission on MystiCast, we&apos;ll act quickly.
        </p>
      ),
    },
    {
      id: "reporting", title: "Reporting Infringement", icon: FileWarning, highlight: "pink",
      body: (
        <>
          <p>If you believe content on MystiCast infringes your copyright, submit a DMCA notice via <Link to="/contact">the contact page</Link>. Include:</p>
          <ul>
            <li>Your physical or electronic signature (a typed name is fine).</li>
            <li>Identification of the copyrighted work you claim was infringed.</li>
            <li>The URL or location of the material you believe is infringing.</li>
            <li>Your full name, address, phone number, and email address.</li>
            <li>A statement that you have a good-faith belief that use of the material is not authorized by the copyright owner, its agent, or the law.</li>
            <li>A statement, under penalty of perjury, that the information is accurate and that you are the copyright owner or authorized to act on their behalf.</li>
          </ul>
        </>
      ),
    },
    {
      id: "what-happens-next", title: "What Happens Next", icon: Info,
      body: (
        <ul>
          <li>We review the notice within <strong>5 business days</strong>.</li>
          <li>If valid, we remove the material and notify the uploader.</li>
          <li>Repeat infringers are terminated per our policy.</li>
        </ul>
      ),
    },
    {
      id: "counter-notices", title: "Counter-Notices", icon: Reply,
      body: (
        <p>
          If you believe content of yours was removed by mistake or misidentification, you may submit a counter-notice.
          Include your contact info, identification of the removed content, and a statement under penalty of perjury that you have a good-faith
          belief the material was removed in error.
        </p>
      ),
    },
    {
      id: "repeat-infringers", title: "Repeat Infringers", icon: UserX, highlight: "orange",
      body: (
        <p>
          MystiCast maintains a policy of terminating, in appropriate circumstances, users who are repeat infringers of copyright.
        </p>
      ),
    },
    {
      id: "music-and-fair-use", title: "Fair Use & Music", icon: Music4,
      body: (
        <>
          <InfoCard icon={Music4} title="🎵 Music on Stream" tone="cyan">
            <p>
              Streaming music you don&apos;t have the right to broadcast is the most common issue. If you plan to play music on stream, use
              royalty-free tracks or content covered by a valid license. This includes commercial music played from Spotify, Apple Music, etc.,
              which is generally <strong>not</strong> licensed for public broadcast.
            </p>
          </InfoCard>
        </>
      ),
    },
    {
      id: "misuse", title: "Misuse of DMCA", icon: AlertTriangle, highlight: "pink",
      body: (
        <p>
          Bad-faith DMCA notices — false claims of ownership, notices used to silence criticism — are themselves violations and may result in
          account termination and civil liability under 17 U.S.C. § 512(f).
        </p>
      ),
    },
    {
      id: "contact", title: "Contact", icon: MessageCircle,
      body: (<p>DMCA notices and questions: use the <Link to="/contact">Contact form</Link> and select &quot;Copyright / DMCA&quot;.</p>),
    },
  ];

  return (
    <LegalPage
      icon={Copyright}
      label="DMCA POLICY"
      title="Copyright & DMCA"
      intro="MystiCast respects creators and intellectual property. Here's how to report copyrighted material and how we respond."
      updated="February 2026"
      sections={sections}
      contact={{
        title: "Submit a DMCA notice",
        body: "Email dmca@mysticast.tv with all required elements below, or use the Contact form. We act on complete notices within 5 business days.",
        cta: { label: "dmca@mysticast.tv", to: "mailto:dmca@mysticast.tv" },
      }}
    />
  );
}
